export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  // cdkTrapFocus adds invisible tabindex="0" anchors around its container to implement focus wrapping.
  // excluded so it doesn't focus and immediately redirect elsewhere via its
  // own focus listener instead of the real first focusable element.
  '[tabindex]:not([tabindex="-1"]):not(.cdk-focus-trap-anchor)',
].join(', ')

export const FocusPositions = ['first', 'last', 'next', 'previous'] as const
export type FocusPosition = (typeof FocusPositions)[number]

/**
 * Focuses an element within a container based on the specified position.
 *
 * @param container - The HTML element containing focusable elements.
 * @param position - The position of the element to focus. Defaults to the first focusable element in container.
 * @param fromElement - If focusing 'next' or 'previous', will be from this element.
 */
export function focusElementInContainer(
  container: HTMLElement | null | undefined,
  position: FocusPosition = 'first',
  fromElement?: EventTarget | null,
): boolean {
  if (!container) return false

  const focusableElements = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
  const firstElement = focusableElements.at(0)
  const lastElement = focusableElements.at(-1)
  let targetElement: Element | undefined

  switch (position) {
    case 'first':
      targetElement = firstElement
      break
    case 'last':
      targetElement = lastElement
      break
    case 'next':
    case 'previous': {
      if (!fromElement) return false

      const currentIndex = focusableElements.findIndex(element => element === fromElement)
      if (currentIndex === -1) return false

      const nextIndex = position === 'next' ? currentIndex + 1 : currentIndex - 1
      targetElement = focusableElements[nextIndex]
      if (!targetElement) return false
      break
    }
    default:
      break
  }

  if (targetElement instanceof HTMLElement) {
    targetElement.focus()
    return true
  }
  return false
}

export function focusRelativeToElement(
  element: HTMLElement,
  position: Exclude<FocusPosition, 'first' | 'last'>,
): boolean {
  const focusableElements = Array.from(document.querySelectorAll(FOCUSABLE_SELECTOR))
  const currentIndex = focusableElements.findIndex(e => e === element)

  const nextIndex = position === 'next' ? currentIndex + 1 : currentIndex - 1
  const targetElement = focusableElements[nextIndex]

  if (targetElement instanceof HTMLElement) {
    targetElement.focus()
    return true
  }
  return false
}
